// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/configuracoes/services/configuracoesService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  ConfigNfe,
  AtualizarConfigNfeRequest,
  ConfigNfce,
  AtualizarConfigNfceRequest,
  ConfigMdfe,
  AtualizarConfigMdfeRequest,
  ConfigCte,
  AtualizarConfigCteRequest,
  ConfigCteOs,
  AtualizarConfigCteOsRequest,
  ConfigCertificado,
  AtualizarConfigCertificadoRequest,
  ConfigSistema,
  AtualizarConfigSistemaRequest,
  ConfigReforma,
  AtualizarConfigReformaRequest,
  EmitenteFiscal,
  EmitenteFiscalCertificado,
  SalvarEmitenteFiscalRequest,
  StatusSefaz,
} from '../types';

const configuracoesService = {
  // ── NF-e ────────────────────────────────────────────────────────────────
  async getConfigNfe(empresaId: number): Promise<ConfigNfe> {
    const { data } = await apiClient.get<ConfigNfe>(`/api/config/nfe/${empresaId}`);
    return data;
  },

  async salvarConfigNfe(empresaId: number, req: AtualizarConfigNfeRequest): Promise<ConfigNfe> {
    const { data } = await apiClient.put<ConfigNfe>(`/api/config/nfe/${empresaId}`, req);
    return data;
  },

  // ── NFC-e ───────────────────────────────────────────────────────────────
  async getConfigNfce(empresaId: number): Promise<ConfigNfce> {
    const { data } = await apiClient.get<ConfigNfce>(`/api/config/nfce/${empresaId}`);
    return data;
  },

  async salvarConfigNfce(empresaId: number, req: AtualizarConfigNfceRequest): Promise<ConfigNfce> {
    const { data } = await apiClient.put<ConfigNfce>(`/api/config/nfce/${empresaId}`, req);
    return data;
  },

  // ── MDF-e ───────────────────────────────────────────────────────────────
  async getConfigMdfe(empresaId: number): Promise<ConfigMdfe> {
    const { data } = await apiClient.get<ConfigMdfe>(`/api/config/mdfe/${empresaId}`);
    return data;
  },

  async salvarConfigMdfe(empresaId: number, req: AtualizarConfigMdfeRequest): Promise<ConfigMdfe> {
    const { data } = await apiClient.put<ConfigMdfe>(`/api/config/mdfe/${empresaId}`, req);
    return data;
  },

  // ── CT-e ────────────────────────────────────────────────────────────────
  async getConfigCte(empresaId: number): Promise<ConfigCte> {
    const { data } = await apiClient.get<ConfigCte>(`/api/config/cte/${empresaId}`);
    return data;
  },

  async salvarConfigCte(empresaId: number, req: AtualizarConfigCteRequest): Promise<ConfigCte> {
    const { data } = await apiClient.put<ConfigCte>(`/api/config/cte/${empresaId}`, req);
    return data;
  },

  // ── CT-e OS ─────────────────────────────────────────────────────────────
  async getConfigCteOs(empresaId: number): Promise<ConfigCteOs> {
    const { data } = await apiClient.get<ConfigCteOs>(`/api/config/cteos/${empresaId}`);
    return data;
  },

  async salvarConfigCteOs(empresaId: number, req: AtualizarConfigCteOsRequest): Promise<ConfigCteOs> {
    const { data } = await apiClient.put<ConfigCteOs>(`/api/config/cteos/${empresaId}`, req);
    return data;
  },

  // ── Certificado ─────────────────────────────────────────────────────────
  async getConfigCertificado(empresaId: number): Promise<ConfigCertificado> {
    const { data } = await apiClient.get<ConfigCertificado>(`/api/config/certificado/${empresaId}`);
    return data;
  },

  async salvarConfigCertificado(
    empresaId: number,
    req: AtualizarConfigCertificadoRequest,
  ): Promise<ConfigCertificado> {
    const { data } = await apiClient.put<ConfigCertificado>(`/api/config/certificado/${empresaId}`, req);
    return data;
  },

  // ── Sistema ─────────────────────────────────────────────────────────────
  async getConfigSistema(empresaId: number): Promise<ConfigSistema> {
    const { data } = await apiClient.get<ConfigSistema>(`/api/config/sistema/${empresaId}`);
    return data;
  },

  async salvarConfigSistema(
    empresaId: number,
    req: AtualizarConfigSistemaRequest,
  ): Promise<ConfigSistema> {
    const { data } = await apiClient.put<ConfigSistema>(`/api/config/sistema/${empresaId}`, req);
    return data;
  },

  // ── Reforma Tributária (IBS/CBS) ────────────────────────────────────────
  async getConfigReforma(empresaId: number): Promise<ConfigReforma> {
    const { data } = await apiClient.get<ConfigReforma>(`/api/config/reforma/${empresaId}`);
    return data;
  },

  async salvarConfigReforma(
    empresaId: number,
    req: AtualizarConfigReformaRequest,
  ): Promise<ConfigReforma> {
    const { data } = await apiClient.put<ConfigReforma>(`/api/config/reforma/${empresaId}`, req);
    return data;
  },

  // ── Emitente fiscal (serviço de emissão + certificado A1) ───────────────
  async getEmitenteFiscal(empresaId: number): Promise<EmitenteFiscal> {
    const { data } = await apiClient.get<EmitenteFiscal>(`/api/config/emitente-fiscal/${empresaId}`);
    return data;
  },

  async salvarEmitenteFiscal(
    empresaId: number,
    req: SalvarEmitenteFiscalRequest,
  ): Promise<EmitenteFiscal> {
    const { data } = await apiClient.put<EmitenteFiscal>(`/api/config/emitente-fiscal/${empresaId}`, req);
    return data;
  },

  async enviarCertificadoFiscal(
    empresaId: number,
    arquivo: File,
    senha: string,
  ): Promise<EmitenteFiscalCertificado> {
    const form = new FormData();
    form.append('arquivo', arquivo);
    form.append('senha', senha);
    const { data } = await apiClient.post<EmitenteFiscalCertificado>(
      `/api/config/emitente-fiscal/${empresaId}/certificado`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  async statusSefaz(empresaId: number): Promise<StatusSefaz> {
    const { data } = await apiClient.get<StatusSefaz>(`/api/config/emitente-fiscal/${empresaId}/status-sefaz`);
    return data;
  },
};

export default configuracoesService;
